import { c as _c } from "react/compiler-runtime";
import { IcoArrowLeftSmall16, IcoFileText16, IcoPlusSmall16 } from '@onefootprint/icons';
import type { SkillFileNode } from '@onefootprint/request-types/dashboard';
import { IconButton, LinkButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import InlineEditableText from '@/components/inline-editable-text';
import { DropOverlay, useDragDropProps } from '../hooks/use-drag-drop-props';
import { useDropTarget } from '../hooks/use-drop-target';
import { useSkillEditor } from '../hooks/use-skill-editor';
import { getAtPath } from '../utils/skills-path';
import SkillActions from './skill-actions';
import SkillDocument from './skill-document';
import { SkillNode } from './skill-node';
type SkillDetailProps = {
  node: SkillFileNode;
  path: string;
  /** Off where the folder name is the skill's identity, like a saved library skill. */
  canRename?: boolean;
  headerActions?: ReactNode;
};
const SkillDetail = t0 => {
  const $ = _c(82);
  const {
    node,
    path,
    canRename: t1,
    headerActions
  } = t0;
  const canRename = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "agentBuilder.skills"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t2);
  const {
    skills,
    selectedPath,
    actions,
    isReadOnly,
    commit
  } = useSkillEditor();
  const {
    dropTarget,
    setDropTarget
  } = useDropTarget();
  const {
    onDrop
  } = useDragDropProps();
  let t3;
  if ($[1] !== selectedPath || $[2] !== skills) {
    t3 = selectedPath ? getAtPath(skills, selectedPath) : undefined;
    $[1] = selectedPath;
    $[2] = skills;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selected = t3;
  let t4;
  if ($[4] !== actions || $[5] !== isReadOnly || $[6] !== node.kind || $[7] !== path || $[8] !== setDropTarget) {
    t4 = event => {
      if (isReadOnly || node.kind !== "folder" || !event.dataTransfer.types.includes("Files")) {
        return;
      }
      event.preventDefault();
      setDropTarget({
        path,
        position: "inside",
        onDrop: nodes => actions.dropExternal(path, "inside", nodes)
      });
    };
    $[4] = actions;
    $[5] = isReadOnly;
    $[6] = node.kind;
    $[7] = path;
    $[8] = setDropTarget;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onDragOver = t4;
  const t5 = isReadOnly ? undefined : onDrop;
  let t6;
  if ($[10] !== dropTarget || $[11] !== path) {
    t6 = dropTarget?.path === path && dropTarget.position === "inside" && <DropOverlay />;
    $[10] = dropTarget;
    $[11] = path;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== actions) {
    t7 = () => actions.select(null);
    $[13] = actions;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <LinkButton variant="label-3" iconComponent={IcoArrowLeftSmall16} iconPosition="left" onClick={t7} className="shrink-0">{t8}</LinkButton>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <span aria-hidden={true} className="text-quaternary">/</span>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = node.name;
  let t12;
  if ($[21] !== t) {
    t12 = t(_temp2);
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t(_temp3);
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t) {
    t14 = t(_temp4);
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== commit) {
    t15 = () => {
      commit();
      return true;
    };
    $[27] = commit;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = canRename && !isReadOnly && node.name !== "SKILL.md";
  const t17 = !node.name && !isReadOnly;
  let t18;
  if ($[29] !== actions || $[30] !== path) {
    t18 = name => actions.rename(path, name);
    $[29] = actions;
    $[30] = path;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== node.name || $[33] !== t12 || $[34] !== t13 || $[35] !== t14 || $[36] !== t15 || $[37] !== t16 || $[38] !== t17 || $[39] !== t18) {
    t19 = <div className="min-w-0 flex-1"><InlineEditableText value={t11} fallback={t12} placeholder={t13} ariaLabel={t14} shouldCloseOnBlur={t15} canEdit={t16} autoFocus={t17} onChange={t18} buttonClassName="max-w-full px-0" textClassName="truncate text-label-2" /></div>;
    $[32] = node.name;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t19 || $[42] !== t9) {
    t20 = <div className="flex min-w-0 flex-auto items-center gap-3">{t9}{t10}{t19}</div>;
    $[41] = t19;
    $[42] = t9;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== isReadOnly || $[45] !== t) {
    t21 = isReadOnly && <span className="text-caption-1 text-tertiary">{t(_temp5)}</span>;
    $[44] = isReadOnly;
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== node || $[48] !== path) {
    t22 = <SkillActions node={node} path={path} />;
    $[47] = node;
    $[48] = path;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== headerActions || $[51] !== t21 || $[52] !== t22) {
    t23 = <div className="ml-auto flex shrink-0 items-center gap-2">{t21}{headerActions}{t22}</div>;
    $[50] = headerActions;
    $[51] = t21;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t20 || $[55] !== t23) {
    t24 = <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-tertiary px-5 py-3">{t20}{t23}</div>;
    $[54] = t20;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = node.kind === "folder";
  let t26;
  if ($[57] !== t25) {
    t26 = cx("grid min-h-0 flex-1 grid-cols-1", {
      "grid-rows-[auto_minmax(0,1fr)] @min-[480px]:grid-cols-[192px_minmax(0,1fr)] @min-[480px]:grid-rows-1 @min-[800px]:grid-cols-[260px_minmax(0,1fr)]": t25
    });
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== actions || $[60] !== isReadOnly || $[61] !== node.children || $[62] !== node.kind || $[63] !== path || $[64] !== t) {
    t27 = node.kind === "folder" && <aside className="flex min-h-0 min-w-0 flex-col border-b border-tertiary bg-canvas @min-[480px]:border-r @min-[480px]:border-b-0" aria-label={t(_temp6)}><div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-tertiary px-4"><h3 className="text-label-3 text-tertiary">{t(_temp7)}</h3>{!isReadOnly && <IconButton icon={IcoPlusSmall16} variant="ghost" size="compact" aria-label={t(_temp8)} onClick={() => actions.addFile(path)} />}</div><div className="max-h-40 min-h-0 flex-1 overflow-y-auto p-2 @min-[480px]:max-h-none">{node.children.map((child, index) => {
          const childPath = `${path}.${index}`;
          return <SkillNode key={childPath} node={child} path={childPath} depth={0} />;
        })}</div>{!isReadOnly && <p className="hidden shrink-0 border-t border-tertiary px-4 py-4 text-caption-1 text-tertiary @min-[480px]:block">{t(_temp9)}</p>}</aside>;
    $[59] = actions;
    $[60] = isReadOnly;
    $[61] = node.children;
    $[62] = node.kind;
    $[63] = path;
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== node.children || $[67] !== node.kind || $[68] !== selected || $[69] !== selectedPath || $[70] !== t) {
    t28 = selected?.kind === "file" && selectedPath ? <SkillDocument key={selectedPath} node={selected} path={selectedPath} /> : <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain"><div className="mx-auto w-full max-w-[800px] px-6 py-8 @min-[800px]:px-12 @min-[800px]:py-10">{node.kind === "folder" && node.children.length > 0 ? <p className="text-body-2 text-tertiary">{t(_temp10)}</p> : <div className="flex min-h-80 flex-col items-start justify-center gap-4"><IcoFileText16 className="text-tertiary" /><div className="space-y-2"><h2 className="text-heading-2 text-primary">{t(_temp11)}</h2><p className="text-body-2 text-tertiary">{t(_temp12)}</p></div></div>}</div></div>;
    $[66] = node.children;
    $[67] = node.kind;
    $[68] = selected;
    $[69] = selectedPath;
    $[70] = t;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== t26 || $[73] !== t27 || $[74] !== t28) {
    t29 = <div className={t26}>{t27}{t28}</div>;
    $[72] = t26;
    $[73] = t27;
    $[74] = t28;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] !== onDragOver || $[77] !== t24 || $[78] !== t29 || $[79] !== t5 || $[80] !== t6) {
    t30 = <div className="@container relative flex min-h-0 min-w-0 flex-1 flex-col" onDragOver={onDragOver} onDrop={t5}>{t6}{t24}{t29}</div>;
    $[76] = onDragOver;
    $[77] = t24;
    $[78] = t29;
    $[79] = t5;
    $[80] = t6;
    $[81] = t30;
  } else {
    t30 = $[81];
  }
  return t30;
};
export default SkillDetail;
function _temp($) {
  return $.back;
}
function _temp2($_0) {
  return $_0.untitled;
}
function _temp3($_1) {
  return $_1.titleField.placeholder;
}
function _temp4($_2) {
  return $_2.titleField.label;
}
function _temp5($_3) {
  return $_3.readOnly;
}
function _temp6($_4) {
  return $_4.files;
}
function _temp7($_5) {
  return $_5.files;
}
function _temp8($_6) {
  return $_6.addFile;
}
function _temp9($_7) {
  return $_7.dropHint;
}
function _temp10($_8) {
  return $_8.noFileSelected;
}
function _temp11($_9) {
  return $_9.noFiles;
}
function _temp12($_10) {
  return $_10.noFilesDescription;
}